import React, { useEffect, useState } from "react";
import axios from "axios";
import API_BASE_URL from "../../../services/api";
import { useAuth } from "../../../context/AuthContext";
import { canAccess } from "../../../utils/permissions";
import {
  PlusCircle,
  Edit2,
  Trash2,
  Search,
  X,
  Check,
  Loader2,
  MapPin,
  ChevronRight,
  Filter
} from "lucide-react";

const API = `${API_BASE_URL}/api/cities`;

const City = () => {
  const { user: currentUser } = useAuth();
  const [cities, setCities] = useState([]);
  const [name, setName] = useState("");
  const [editId, setEditId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const fetchCities = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(API);
      setCities(res.data.data);
    } catch (err) {
      setError("Failed to fetch cities. Please try again.");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      setError("City name cannot be empty");
      return;
    }

    setSubmitLoading(true);
    setError(null);
    setSuccess(null);

    try {
      if (editId) {
        await axios.put(`${API}/${editId}`, { name });
        setSuccess("City updated successfully!");
      } else {
        await axios.post(API, { name });
        setSuccess("City added successfully!");
      }

      setName("");
      setEditId(null);
      fetchCities();
      
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.response?.data?.message || "An error occurred. Please try again.");
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleEdit = (city) => {
    setName(city.name);
    setEditId(city.id);
    const formElement = document.getElementById("city-form");
    if (formElement) {
      formElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this city?")) return;
    
    try {
      await axios.delete(`${API}/${id}`);
      setSuccess("City deleted successfully!");
      fetchCities();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError("Failed to delete city. Please try again.");
    }
  };

  const handleCancel = () => {
    setName("");
    setEditId(null);
    setError(null);
  };

  const filteredCities = cities.filter(city =>
    city.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  useEffect(() => {
    fetchCities();
  }, []);

  return (
    <div className="min-h-screen ">
      <div >
     

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Left Column - Form */}
          <div className="lg:col-span-1" id="city-form">
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-5">
              <div className="flex items-center gap-2 mb-5">
                <div className="p-1.5 bg-blue-50 rounded-md">
                  <MapPin className="w-4 h-4 text-blue-600" />
                </div>
                <h2 className="text-base font-semibold text-gray-800">
                  {editId ? "Edit City" : "Add City"}
                </h2>
              </div>
              
              {/* Messages */}
              {success && (
                <div className="mb-4 p-2.5 bg-green-50 border border-green-100 rounded-md flex items-center gap-2 text-xs">
                  <Check className="w-3.5 h-3.5 text-green-600" />
                  <span className="text-green-700">{success}</span>
                </div>
              )}

              {error && (
                <div className="mb-4 p-2.5 bg-red-50 border border-red-100 rounded-md flex items-center gap-2 text-xs">
                  <X className="w-3.5 h-3.5 text-red-600" />
                  <span className="text-red-700">{error}</span>
                </div>
              )}

              {/* Form */}
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1.5">
                    City Name
                  </label>
                  <input
                    type="text"
                    placeholder="Enter city name"
                    value={name}
                    onChange={(e) => {
                      setName(e.target.value);
                      setError(null);
                    }}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200 outline-none"
                    disabled={submitLoading}
                  />
                </div>

                <div className="flex gap-2 pt-1">
                  <button
                    type="submit"
                    disabled={submitLoading || !name.trim() || (editId ? !canAccess(currentUser, "canEdit") : !canAccess(currentUser, "canCreate"))}
                    className="flex-1 bg-blue-600 text-white px-4 py-2 text-sm font-medium rounded-md hover:bg-blue-700 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-1.5"
                  >
                    {submitLoading ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : editId ? (
                      <>
                        <Edit2 className="w-3.5 h-3.5" />
                        Update
                      </>
                    ) : (
                      <>
                        <PlusCircle className="w-3.5 h-3.5" />
                        Add City
                      </>
                    )}
                  </button>
                  
                  {editId && (
                    <button
                      type="button"
                      onClick={handleCancel}
                      className="px-3 py-2 text-sm border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors duration-200"
                      disabled={submitLoading}
                    >
                      Cancel
                    </button>
                  )}
                </div>
              </form>

              {/* Stats */}
              <div className="mt-6 pt-5 border-t border-gray-100">
                <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-3">
                  Overview
                </h3>
                <div className="grid grid-cols-2 gap-2">
                  <div className="bg-gray-50 p-3 rounded-md border border-gray-100">
                    <div className="text-lg font-semibold text-gray-800">{cities.length}</div>
                    <div className="text-xs text-gray-500 mt-0.5">Total Cities</div>
                  </div>
                  <div className="bg-blue-50 p-3 rounded-md border border-blue-100">
                    <div className="text-lg font-semibold text-blue-700">{filteredCities.length}</div>
                    <div className="text-xs text-blue-600 mt-0.5">Displayed</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Cities List */}
          <div className="lg:col-span-3">
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
              {/* Table Header */}
              <div className="p-5 border-b border-gray-200">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                  <div>
                    <h2 className="text-base font-semibold text-gray-800">Cities</h2>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {filteredCities.length} of {cities.length} cities
                    </p>
                  </div>
                  
                  <div className="relative w-full sm:w-56">
                    <Search className="absolute left-2.5 top-1/2 transform -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search cities..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200 outline-none"
                    />
                    {searchTerm && (
                      <button
                        onClick={() => setSearchTerm("")}
                        className="absolute right-2.5 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Table Content */}
              <div className="overflow-hidden">
                {loading ? (
                  <div className="flex flex-col items-center justify-center py-12">
                    <Loader2 className="w-6 h-6 text-blue-600 animate-spin mb-2" />
                    <p className="text-sm text-gray-600">Loading cities...</p>
                  </div>
                ) : filteredCities.length === 0 ? (
                  <div className="text-center py-10">
                    <div className="mx-auto w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-3">
                      <Filter className="w-5 h-5 text-gray-400" />
                    </div>
                    <h3 className="text-sm font-medium text-gray-700 mb-1">
                      {searchTerm ? "No cities found" : "No cities yet"}
                    </h3>
                    <p className="text-xs text-gray-500 max-w-xs mx-auto">
                      {searchTerm
                        ? "No cities match your search criteria"
                        : "Get started by adding your first city"}
                    </p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="py-3 px-4 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-b border-gray-200">
                            <div className="flex items-center gap-1">
                              <MapPin className="w-3.5 h-3.5" />
                              City Name
                            </div>
                          </th>
                          <th className="py-3 px-4 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-b border-gray-200 w-28">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {filteredCities.map((city) => (
                          <tr 
                            key={city.id} 
                            className="hover:bg-gray-50 transition-colors duration-150"
                          >
                            <td className="py-3 px-4">
                              <div className="flex items-center gap-2.5">
                                <div className="flex-shrink-0 w-6 h-6 bg-blue-50 rounded flex items-center justify-center">
                                  <span className="text-xs font-medium text-blue-600">
                                    {city.name.charAt(0).toUpperCase()}
                                  </span>
                                </div>
                                <span className="text-sm text-gray-800 font-medium">
                                  {city.name}
                                </span>
                              </div>
                            </td>
                            <td className="py-3 px-4">
                              <div className="flex items-center gap-1">
                                <button
                                  onClick={() => canAccess(currentUser, "canEdit") && handleEdit(city)}
                                  disabled={!canAccess(currentUser, "canEdit")}
                                  className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-md transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                                  title="Edit"
                                >
                                  <Edit2 className="w-3.5 h-3.5" />
                                </button>
                                <button
                                  onClick={() => canAccess(currentUser, "canDelete") && handleDelete(city.id)}
                                  disabled={!canAccess(currentUser, "canDelete")}
                                  className="p-1.5 text-red-600 hover:bg-red-50 rounded-md transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                                  title="Delete"
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Table Footer */}
              {filteredCities.length > 0 && (
                <div className="px-5 py-3 border-t border-gray-200">
                  <div className="flex items-center justify-between text-xs text-gray-500">
                    <div>
                      Showing <span className="font-medium">{filteredCities.length}</span> cities
                      {searchTerm && (
                        <span className="ml-2">
                          for "<span className="font-medium">{searchTerm}</span>"
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-1">
                        <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                        <span>Active</span>
                      </div>
                      <div className="text-gray-400">|</div>
                      <div>Last updated: {new Date().toLocaleDateString()}</div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Help Text */}
            <div className="mt-4 text-xs text-gray-500">
              <p>• Click edit to modify a city name</p>
              <p className="mt-0.5">• Use the search to filter cities quickly</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default City;